import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Heading6 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { CaretIcon } from '@ui/icons/Caret.icon'

import { Box } from '@shared/components/Box'
import { getFundingCurrencyIcon } from '@shared/components/Funding/FundingCurrencyIcons'

interface Props {
  options?: any[]
  value?: string
  onSelect?: (value: string) => void
  label?: string
}

export const FundingCurrencyTitle: React.FC<Props> = (props) => {
  const { options = [], value, onSelect, label } = props
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()
  const CurrentIcon = getFundingCurrencyIcon(value)
  const hasOptions = options.length > 1
  const selectedOptionName = useMemo(
    () => options.find((o) => o.id === value)?.name ?? value,
    [options, value]
  )

  const content = (
    <Box flex align='center' gap='x8'>
      <Heading6 medium>{label || t('Deposit')}</Heading6>
      <Box flex align='center' gap='x4'>
        {CurrentIcon && <CurrentIcon size={24} />}
        <Heading6 medium>{selectedOptionName}</Heading6>
        {hasOptions && (
          <IconButton.Secondary
            size='sm'
            icon={isOpen ? CaretIcon.Up : CaretIcon.Down}
            onClick={() => setIsOpen(!isOpen)}
          />
        )}
      </Box>
    </Box>
  )

  if (!hasOptions) {
    return content
  }

  return (
    <Popover
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      content={
        <PopoverCard>
          <PopoverList variant='compact'>
            {options.map((option) => {
              const CurrencyIcon = getFundingCurrencyIcon(option.id)

              return (
                <ListItem
                  key={option.id}
                  active={option.id === value}
                  endSlot={{ radio: true }}
                  startSlot={CurrencyIcon ? { icon: CurrencyIcon } : undefined}
                  onClick={() => {
                    onSelect?.(option.id)
                    setIsOpen(false)
                  }}
                >
                  {option.name}
                </ListItem>
              )
            })}
          </PopoverList>
        </PopoverCard>
      }
    >
      {content}
    </Popover>
  )
}
